/**
 * Async Handler Manager
 *
 * Manages asynchronous handler promises and provides a clean API
 * for tracking loading state and waiting for all handlers to complete.
 */

import { FieldPath } from './types';

interface AsyncUpdate {
  path: FieldPath;
  value: any;
}

/**
 * Manages asynchronous handler promises
 */
export class AsyncHandlerManager {
  private promises = new Map<FieldPath, Promise<AsyncUpdate>>();
  private latestPromises = new Map<FieldPath, Promise<AsyncUpdate>>();
  private promisePaths = new WeakMap<Promise<AsyncUpdate>, FieldPath>();

  /**
   * Adds an async handler promise for a field
   *
   * @param path - The field path
   * @param promise - The promise that resolves to the field value
   */
  add(path: FieldPath, promise: Promise<any>): Promise<AsyncUpdate> {
    // Wrap the promise to include the path in the result
    const wrappedPromise = promise.then(value => {
      return {
        path,
        value,
      };
    });

    this.promises.set(path, wrappedPromise);
    this.latestPromises.set(path, wrappedPromise);
    this.promisePaths.set(wrappedPromise, path);
    return wrappedPromise;
  }

  isCurrent(promise: Promise<AsyncUpdate>): boolean {
    const path = this.promisePaths.get(promise);
    return path !== undefined && this.latestPromises.get(path) === promise;
  }

  invalidate(path: FieldPath): void {
    this.promises.delete(path);
    this.latestPromises.delete(path);
  }

  removePending(promise: Promise<AsyncUpdate>): void {
    const path = this.promisePaths.get(promise);
    if (path !== undefined && this.promises.get(path) === promise) {
      this.promises.delete(path);
    }
  }

  complete(promise: Promise<AsyncUpdate>): void {
    this.removePending(promise);
    const path = this.promisePaths.get(promise);
    if (path !== undefined && this.latestPromises.get(path) === promise) {
      this.latestPromises.delete(path);
    }
  }

  takePendingPromises(): Array<Promise<AsyncUpdate>> {
    const promises = this.getPendingPromises();
    this.promises.clear();
    return promises;
  }

  /**
   * Checks if there are any pending async handlers
   *
   * @returns True if loading, false otherwise
   */
  isLoading(): boolean {
    return this.latestPromises.size > 0;
  }

  /**
   * Gets the number of pending async handlers
   *
   * @returns Count of pending handlers
   */
  getPendingCount(): number {
    return this.latestPromises.size;
  }

  /**
   * Waits for all pending async handlers to complete
   * and returns their results
   *
   * @returns Promise that resolves to array of updates
   */
  async waitAll(): Promise<AsyncUpdate[]> {
    if (this.promises.size === 0) {
      return [];
    }

    const promiseArray = this.takePendingPromises();

    try {
      const results = await Promise.all(promiseArray);
      return results.filter((_result, index) =>
        this.isCurrent(promiseArray[index]),
      );
    } finally {
      promiseArray.forEach(promise => this.complete(promise));
    }
  }

  /**
   * Gets all pending promises (without clearing them)
   * Useful for integration with existing code
   *
   * @returns Array of promises
   */
  getPendingPromises(): Array<Promise<AsyncUpdate>> {
    return Array.from(this.promises.values());
  }

  /**
   * Clears all pending promises
   */
  clear(): void {
    this.promises.clear();
    this.latestPromises.clear();
  }

  /**
   * Gets all pending field paths
   *
   * @returns Array of field paths with pending handlers
   */
  getPendingPaths(): FieldPath[] {
    return Array.from(this.promises.keys());
  }
}
